<script>
/* eslint-disable no-console */
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import axios from "axios";
import VueBootstrapAutocomplete from "@vue-bootstrap-components/vue-bootstrap-autocomplete";
import { toast, variants } from "@/helpers/toaster";
import ErrorAlert from "@/components/errorAlert";

export default {
  page: {
    title: "City Express",
  },
  components: {
    PageHeader,
    Layout,
    ErrorAlert,
    VueBootstrapAutocomplete,
  },
  data() {
    return {
      title: "City Express",
      activeTab: "shipments",

      // Shipments list
      shipments: [],
      shipmentFields: [
        { key: "ShipmentID", label: "ID Pošiljke", sortable: true },
        { key: "Ref1", label: "Referenca", sortable: false },
        { key: "Cosignee.Name", label: "Primalac", sortable: false },
        { key: "Cosignee.Place", label: "Mesto", sortable: false },
        { key: "APIStatus", label: "Status", sortable: false },
        { key: "CreateDate", label: "Datum", sortable: true },
        {
          key: "actions",
          label: "",
          sortable: false,
          thClass: "text-right",
          tdClass: "text-right",
        },
      ],
      shipmentCurrentPage: 1,
      shipmentPerPage: 15,
      shipmentSortBy: "CreateDate",
      shipmentSortDesc: true,

      // Create shipment form
      form: {
        sender: {
          name: "",
          countryCode: "RS",
          zipcode: "",
          city: "",
          streetAndNumber: "",
        },
        cosignee: {
          name: "",
          countryCode: "RS",
          zipcode: "",
          city: "",
          streetAndNumber: "",
          telephone: "",
          notifyGSM: "",
          notifyEmail: "",
        },
        numberOfCollies: 1,
        unitType: "Weight",
        unitAmount: null,
        ref1: "",
        ref2: "",
        ref3: "",
        codValue: null,
        codCurrency: null,
        remark: "",
        pickupRemark: "",
        deliveryRemark: "",
        cargo: false,
        returnDocument: false,
      },

      // Shipment detail modal
      showDetailModal: false,
      selectedShipment: null,
      selectedShipmentEvents: [],

      // Label modal
      showLabelModal: false,
      labelPdfUrl: null,

      // Tracking
      trackingShipmentId: "",
      trackingResult: null,

      // Wings Portal prefill
      kupacQuery: "",
      kupci: [],
      selectedProfakture: [], // Array of selected profaktura (max 3)
      orderDocuments: [], // Corresponding order documents
      loadingOrderDoc: false,

      // Profaktura detail modal
      showProfakturaModal: false,
      profakturaModalDoc: null,

      // Address picker (when multiple PFs have different addresses)
      showAddressPicker: false,
      availableAddresses: [],
    };
  },
  computed: {
    // Filter kupci list to only show profakture from the same kupac (if one is already selected)
    filteredKupci() {
      if (this.selectedProfakture.length === 0) return this.kupci;
      const firstKupac = this.selectedProfakture[0].kupac;
      return this.kupci.filter(
        (k) =>
          k.kupac === firstKupac &&
          !this.selectedProfakture.find((s) => s.orderId === k.orderId),
      );
    },
    canAddMoreProfakture() {
      return this.selectedProfakture.length < 3;
    },
  },
  watch: {},
  async created() {
    await this.loadShipments();
    await this.loadKupci();
  },
  methods: {
    // ---- Shipments Tab ----
    async loadShipments() {
      this.$store.commit("loader/START_LOADING");
      try {
        const { data } = await axios.get("/super-admin/cityexpress/shipments");
        this.shipments = data.data && Array.isArray(data.data) ? data.data : [];
      } catch (e) {
        console.error("Error fetching shipments:", e);
      } finally {
        this.$store.commit("loader/END_LOADING");
      }
    },

    async viewShipment(shipment) {
      this.$store.commit("loader/START_LOADING");
      try {
        const shipmentId = shipment.ShipmentID;
        const { data } = await axios.get(
          `/super-admin/cityexpress/shipments/${shipmentId}`,
        );
        this.selectedShipment = data.data || data;
        this.selectedShipmentEvents = this.selectedShipment.Events || [];
        this.showDetailModal = true;
      } catch (e) {
        console.error("Error fetching shipment details:", e);
      } finally {
        this.$store.commit("loader/END_LOADING");
      }
    },

    async commitAndPrintLabel(shipment) {
      this.$store.commit("loader/START_LOADING");
      try {
        const { data } = await axios.post(
          "/super-admin/cityexpress/shipments/commit",
          {
            shipmentIds: [shipment.ShipmentID],
          },
        );
        if (data.labelsbase64 || data.Labelsbase64) {
          this.openPdfFromBase64(data.labelsbase64 || data.Labelsbase64);
          toast("Nalepnica je uspešno generisana.", variants.SUCCESS);
        } else {
          toast(
            "Pošiljka je ubeležena, ali nalepnica nije dostupna.",
            variants.WARNING,
          );
        }
        await this.loadShipments();
      } catch (e) {
        console.error("Error committing shipment:", e);
      } finally {
        this.$store.commit("loader/END_LOADING");
      }
    },

    async reprintLabel(shipment) {
      this.$store.commit("loader/START_LOADING");
      try {
        const { data } = await axios.post(
          "/super-admin/cityexpress/shipments/reprint",
          {
            shipmentIds: [shipment.ShipmentID],
          },
        );
        if (data.labelsbase64 || data.Labelsbase64) {
          this.openPdfFromBase64(data.labelsbase64 || data.Labelsbase64);
          toast("Nalepnica je ponovo odštampana.", variants.SUCCESS);
        } else {
          toast("Nalepnica nije dostupna.", variants.WARNING);
        }
      } catch (e) {
        console.error("Error reprinting label:", e);
      } finally {
        this.$store.commit("loader/END_LOADING");
      }
    },

    async cancelShipment(shipment) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Da li ste sigurni da želite da otkažete pošiljku #${shipment.ShipmentID}?`,
        {
          title: "Potvrda otkazivanja",
          okVariant: "danger",
          okTitle: "Otkaži pošiljku",
          cancelTitle: "Nazad",
          cancelVariant: "secondary",
          centered: true,
        },
      );
      if (!confirmed) return;

      this.$store.commit("loader/START_LOADING");
      try {
        await axios.post("/super-admin/cityexpress/shipments/cancel", {
          shipmentIds: [shipment.ShipmentID],
        });
        toast("Pošiljka je uspešno otkazana.", variants.SUCCESS);
        await this.loadShipments();
      } catch (e) {
        console.error("Error cancelling shipment:", e);
      } finally {
        this.$store.commit("loader/END_LOADING");
      }
    },

    openPdfFromBase64(base64) {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    },

    formatDateTime(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    },

    getStatusVariant(status) {
      const s = Number(status);
      // Delivered / picked up / completed
      if ([40, 44, 66, 70, 71, 72, 73, 134, 441, 470].includes(s))
        return "success";
      // In transit / processing
      if ([37, 50, 60, 65, 290].includes(s)) return "primary";
      // Initial / data entry
      if ([0, 8, 10].includes(s)) return "info";
      // Refused / return to sender
      if ([100, 260].includes(s)) return "danger";
      // Problems / delays / address issues / absent
      if (
        [
          39,
          75,
          76,
          77,
          78,
          80,
          90,
          95,
          96,
          110,
          120,
          130,
          131,
          140,
          291,
          499,
        ].includes(s)
      )
        return "warning";
      return "secondary";
    },

    getStatusLabel(status, shipment) {
      // Use latest event's StatusName if available
      if (shipment && shipment.Events && shipment.Events.length > 0) {
        const latest = shipment.Events[shipment.Events.length - 1];
        if (latest.StatusName) return latest.StatusName;
      }
      // Also check APIStatusName field
      if (shipment && shipment.APIStatusName) return shipment.APIStatusName;

      const statusMap = {
        0: "Definicija preuzimanja",
        8: "Definicija podataka",
        10: "Pošiljka uneta u sistem",
        37: "Preuzimanje na drugoj adresi u toku dana",
        39: "Nema paketa za preuzimanje",
        40: "Isporučeno",
        44: "Preuzeto",
        50: "Vraćeno u lokalni centar",
        60: "Stavljeno na skladište",
        65: "Otpremljeno iz skladišta",
        66: "Preuzet povratni dokument",
        70: "Isporučeno (deponovano)",
        71: "Deponovano na parcel shop",
        72: "Isporuka na poštanski terminal",
        73: "Isporučeno u lokalnom centru",
        75: "Nema preuzimanja - neizvodljivo",
        76: "Pošiljka nije spremna za preuzimanje",
        77: "Pošiljalac odsutan",
        78: "Nepoznata adresa pošiljaoca",
        80: "Nepoznata adresa",
        90: "Nedostatak vremena",
        95: "Vanredna situacija",
        96: "Pogrešno sortirano",
        100: "Odbijeno",
        110: "Nepoznat primalac",
        120: "Pošiljka je neuručiva",
        130: "Primalac odsutan",
        131: "Odložena isporuka, primalac odsutan",
        134: "Dostavljena povratna pošiljka",
        140: "Godišnji odmor",
        260: "Vratiti pošiljaocu",
        290: "Prerutirano",
        291: "Pogrešno rutirano",
        441: "Preuzeto sa Parcel Shop-a",
        470: "Pošiljalac dostavio na Parcel Shop",
        499: "Period zadržavanja u parcel shop-u je istekao",
      };
      return statusMap[Number(status)] || `Status ${status}` || "—";
    },

    // ---- Create Shipment Tab ----
    async submitShipment() {
      this.$store.commit("errorBag/CLEAR");
      this.$store.commit("loader/START_LOADING");
      try {
        const payload = {
          sender: { ...this.form.sender },
          cosignee: { ...this.form.cosignee },
          numberOfCollies: this.form.numberOfCollies,
        };

        if (this.form.unitType) payload.unitType = this.form.unitType;
        if (this.form.unitAmount) payload.unitAmount = this.form.unitAmount;
        if (this.form.ref1) payload.ref1 = this.form.ref1;
        if (this.form.ref2) payload.ref2 = this.form.ref2;
        if (this.form.ref3) payload.ref3 = this.form.ref3;
        if (this.form.codValue) payload.codValue = this.form.codValue;
        if (this.form.codCurrency) payload.codCurrency = this.form.codCurrency;
        if (this.form.remark) payload.remark = this.form.remark;
        if (this.form.pickupRemark)
          payload.pickupRemark = this.form.pickupRemark;
        if (this.form.deliveryRemark)
          payload.deliveryRemark = this.form.deliveryRemark;
        if (this.form.cargo) payload.cargo = true;
        if (this.form.returnDocument) payload.returnDocument = true;

        const { data } = await axios.post(
          "/super-admin/cityexpress/shipments",
          payload,
        );

        if (data.status === "OK" || data.status === 0) {
          toast(
            `Pošiljka je uspešno kreirana. ID: ${data.shipmentid}`,
            variants.SUCCESS,
          );
          this.resetForm();
          this.activeTab = "shipments";
          await this.loadShipments();
        } else {
          const errorMsg =
            data.error?.message ||
            (data.validations || []).map((v) => v.message).join(", ") ||
            "Greška pri kreiranju pošiljke.";
          toast(errorMsg, variants.ERROR);
        }
      } catch (e) {
        console.error("Error creating shipment:", e);
      } finally {
        this.$store.commit("loader/END_LOADING");
      }
    },

    async loadKupci() {
      try {
        const { data } = await axios.get(
          "/super-admin/wings/city-express-customers",
        );
        this.kupci = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error("Error fetching kupci:", e);
      }
    },

    async onKupacSelected(kupac) {
      if (this.selectedProfakture.length >= 3) {
        toast("Maksimalno 3 profakture po pošiljci.", variants.WARNING);
        return;
      }

      // Enforce same kupac
      if (
        this.selectedProfakture.length > 0 &&
        kupac.kupac !== this.selectedProfakture[0].kupac
      ) {
        toast("Sve profakture moraju biti za istog kupca.", variants.WARNING);
        return;
      }

      // Prevent duplicates
      if (this.selectedProfakture.find((s) => s.orderId === kupac.orderId)) {
        return;
      }

      this.loadingOrderDoc = true;
      try {
        const { data } = await axios.get(
          `/super-admin/wings/order-document/${kupac.orderId}`,
        );
        this.selectedProfakture.push(kupac);
        this.orderDocuments.push(data);
        this.rebuildFromDocuments();
      } catch (e) {
        console.error("Error fetching order document:", e);
        toast("Greška pri dohvatanju podataka kupca.", variants.ERROR);
      } finally {
        this.loadingOrderDoc = false;
        this.kupacQuery = "";
      }
    },

    removeProfaktura(index) {
      this.selectedProfakture.splice(index, 1);
      this.orderDocuments.splice(index, 1);
      if (this.selectedProfakture.length === 0) {
        this.clearPrefill();
      } else {
        this.rebuildFromDocuments();
      }
    },

    rebuildFromDocuments() {
      if (this.orderDocuments.length === 0) return;

      // Sender from first document
      const firstDoc = this.orderDocuments[0];
      if (firstDoc.sender) {
        this.form.sender.name = firstDoc.sender.name || "";
        this.form.sender.zipcode = firstDoc.sender.zipcode || "";
        this.form.sender.city = firstDoc.sender.city || "";
        this.form.sender.streetAndNumber =
          firstDoc.sender.streetAndNumber || "";
        this.form.sender.countryCode = firstDoc.sender.countryCode || "RS";
      }

      // Cosignee from first document
      if (firstDoc.cosignee) {
        this.form.cosignee.name = firstDoc.cosignee.name || "";
        this.form.cosignee.zipcode = firstDoc.cosignee.zipcode || "";
        this.form.cosignee.city = firstDoc.cosignee.city || "";
        this.form.cosignee.streetAndNumber =
          firstDoc.cosignee.streetAndNumber || "";
        this.form.cosignee.countryCode = firstDoc.cosignee.countryCode || "RS";
      }

      // Ref1 = semicolon-joined document numbers
      this.form.ref1 = this.orderDocuments
        .map((d) => d.dokument)
        .filter(Boolean)
        .join(";");

      // Check if cosignee addresses differ across documents
      if (this.orderDocuments.length > 1) {
        const addresses = this.orderDocuments.map((d) => ({
          label: `${d.dokument} — ${d.cosignee.name}, ${d.cosignee.streetAndNumber}, ${d.cosignee.zipcode} ${d.cosignee.city}`,
          cosignee: { ...d.cosignee },
        }));
        const first = JSON.stringify(this.orderDocuments[0].cosignee);
        const allSame = this.orderDocuments.every(
          (d) => JSON.stringify(d.cosignee) === first,
        );
        if (!allSame) {
          this.availableAddresses = addresses;
          this.showAddressPicker = true;
        }
      }
    },

    clearPrefill() {
      this.kupacQuery = "";
      this.selectedProfakture = [];
      this.orderDocuments = [];
      this.form.sender = {
        name: "",
        countryCode: "RS",
        zipcode: "",
        city: "",
        streetAndNumber: "",
      };
      this.form.cosignee = {
        name: "",
        countryCode: "RS",
        zipcode: "",
        city: "",
        streetAndNumber: "",
        telephone: "",
        notifyGSM: "",
        notifyEmail: "",
      };
      this.form.ref1 = "";
      this.form.codValue = null;
      this.showAddressPicker = false;
      this.availableAddresses = [];
    },

    openProfakturaModal(index) {
      this.profakturaModalDoc = this.orderDocuments[index];
      this.showProfakturaModal = true;
    },

    selectAddress(addr) {
      this.form.cosignee.name = addr.cosignee.name || "";
      this.form.cosignee.zipcode = addr.cosignee.zipcode || "";
      this.form.cosignee.city = addr.cosignee.city || "";
      this.form.cosignee.streetAndNumber = addr.cosignee.streetAndNumber || "";
      this.form.cosignee.countryCode = addr.cosignee.countryCode || "RS";
      this.showAddressPicker = false;
      toast("Adresa primaoca je ažurirana.", variants.SUCCESS);
    },

    formatCurrency(val) {
      if (val == null) return "0,00";
      return Number(val).toLocaleString("sr-RS", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    resetForm() {
      this.kupacQuery = "";
      this.selectedProfakture = [];
      this.orderDocuments = [];
      this.showAddressPicker = false;
      this.availableAddresses = [];
      this.form = {
        sender: {
          name: "",
          countryCode: "RS",
          zipcode: "",
          city: "",
          streetAndNumber: "",
        },
        cosignee: {
          name: "",
          countryCode: "RS",
          zipcode: "",
          city: "",
          streetAndNumber: "",
          telephone: "",
          notifyGSM: "",
          notifyEmail: "",
        },
        numberOfCollies: 1,
        unitType: "Weight",
        unitAmount: null,
        ref1: "",
        ref2: "",
        ref3: "",
        codValue: null,
        codCurrency: null,
        remark: "",
        pickupRemark: "",
        deliveryRemark: "",
        cargo: false,
        returnDocument: false,
      };
    },

    // ---- Tracking Tab ----
    async trackShipment() {
      if (!this.trackingShipmentId) {
        toast("Unesite ID pošiljke.", variants.ERROR);
        return;
      }
      this.$store.commit("loader/START_LOADING");
      try {
        const { data } = await axios.get(
          `/super-admin/cityexpress/shipments/${this.trackingShipmentId}`,
        );
        this.trackingResult = data.data || data;
      } catch (e) {
        console.error("Error tracking shipment:", e);
        this.trackingResult = null;
      } finally {
        this.$store.commit("loader/END_LOADING");
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" />

    <!-- Tabs -->
    <b-tabs v-model="activeTab" content-class="mt-3" nav-class="nav-tabs">
      <!-- Tab: Shipments List -->
      <b-tab title="Pošiljke" active>
        <div class="row mb-2">
          <div class="col-12 d-flex align-items-end justify-content-end">
            <b-button
              variant="outline-primary"
              size="sm"
              @click="loadShipments"
            >
              <i class="fas fa-sync-alt"></i> Osveži
            </b-button>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <b-table
              :items="shipments"
              :fields="shipmentFields"
              :per-page="shipmentPerPage"
              :current-page="shipmentCurrentPage"
              :sort-by.sync="shipmentSortBy"
              :sort-desc.sync="shipmentSortDesc"
              striped
              responsive
              show-empty
              empty-text="Nema pošiljki."
              class="border-left border-right border-bottom"
            >
              <template v-slot:cell(CreateDate)="data">
                {{ formatDate(data.item.CreateDate) }}
              </template>

              <template v-slot:cell(APIStatus)="data">
                <b-badge :variant="getStatusVariant(data.item.APIStatus)">
                  {{ getStatusLabel(data.item.APIStatus, data.item) }}
                </b-badge>
              </template>

              <template v-slot:cell(actions)="data">
                <b-button
                  size="sm"
                  variant="outline-info"
                  class="mr-1"
                  title="Detalji"
                  @click="viewShipment(data.item)"
                >
                  <i class="fas fa-eye"></i>
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-success"
                  class="mr-1"
                  title="Ubeleži i štampaj nalepnicu"
                  @click="commitAndPrintLabel(data.item)"
                >
                  <i class="fas fa-print"></i>
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="mr-1"
                  title="Ponovo štampaj nalepnicu"
                  @click="reprintLabel(data.item)"
                >
                  <i class="fas fa-redo"></i>
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  title="Otkaži pošiljku"
                  @click="cancelShipment(data.item)"
                >
                  <i class="fas fa-times"></i>
                </b-button>
              </template>
            </b-table>
            <b-pagination
              v-if="shipments.length > shipmentPerPage"
              v-model="shipmentCurrentPage"
              :total-rows="shipments.length"
              :per-page="shipmentPerPage"
              class="mt-3"
              align="center"
            />
          </div>
        </div>
      </b-tab>

      <!-- Tab: Create Shipment -->
      <b-tab title="Nova pošiljka">
        <div class="card">
          <div class="card-body">
            <ErrorAlert />
            <b-form @submit.prevent="submitShipment()">
              <div class="row">
                <div class="col-md-12">
                  <h5 class="mb-3">Profakture (max 3)</h5>
                  <div class="row mb-3">
                    <div class="col-md-6">
                      <div class="form-group mb-0">
                        <label v-if="canAddMoreProfakture"
                          >Dodaj profakturu</label
                        >
                        <VueBootstrapAutocomplete
                          v-if="canAddMoreProfakture"
                          class="autocomplete"
                          v-model="kupacQuery"
                          :data="filteredKupci"
                          :serializer="
                            (item) => `${item.kupac} (${item.dokument})`
                          "
                          :minMatchingChars="0"
                          showOnFocus
                          placeholder="Pretražite kupca..."
                          @hit="onKupacSelected($event)"
                        />
                        <small v-else class="text-muted"
                          >Maksimalan broj profaktura je dodat.</small
                        >
                      </div>
                    </div>
                    <div class="col-md-3 d-flex align-items-end">
                      <b-button
                        v-if="selectedProfakture.length > 0"
                        variant="outline-secondary"
                        size="sm"
                        @click="clearPrefill()"
                      >
                        <i class="fas fa-times"></i> Obriši sve
                      </b-button>
                    </div>
                    <div class="col-md-3 d-flex align-items-end">
                      <div v-if="loadingOrderDoc" class="text-muted">
                        <i class="fas fa-spinner fa-spin"></i> Učitavanje...
                      </div>
                    </div>
                  </div>

                  <!-- Selected profakture tags with values -->
                  <div v-if="selectedProfakture.length > 0" class="row mb-3">
                    <div class="col-md-12">
                      <div class="d-flex flex-wrap gap-2 mb-2">
                        <b-badge
                          v-for="(pf, idx) in selectedProfakture"
                          :key="pf.orderId"
                          variant="info"
                          class="px-3 py-2 mr-2 mb-1"
                          style="font-size: 0.9em; cursor: pointer"
                          @click="openProfakturaModal(idx)"
                        >
                          {{ pf.dokument }}
                          <span v-if="orderDocuments[idx]" class="ml-1">
                            —
                            {{
                              formatCurrency(orderDocuments[idx].total.uplatiti)
                            }}
                            RSD
                          </span>
                          <span
                            class="ml-2"
                            style="cursor: pointer"
                            @click.stop="removeProfaktura(idx)"
                            >×</span
                          >
                        </b-badge>
                      </div>
                      <small class="text-muted">
                        Kupac:
                        <strong>{{ orderDocuments[0].kupac.naziv }}</strong>
                        <span v-if="orderDocuments.length > 1">
                          | Ukupno:
                          {{
                            formatCurrency(
                              orderDocuments.reduce(
                                (s, d) =>
                                  s +
                                  (d.total && d.total.uplatiti
                                    ? d.total.uplatiti
                                    : 0),
                                0,
                              ),
                            )
                          }}
                          RSD
                        </span>
                        <span class="ml-2" style="font-style: italic"
                          >(kliknite na profakturu za detalje)</span
                        >
                      </small>
                    </div>
                  </div>

                  <!-- Address picker when profakture have different delivery addresses -->
                  <div v-if="showAddressPicker" class="row mb-3">
                    <div class="col-md-12">
                      <div class="alert alert-warning mb-0 py-2">
                        <strong
                          ><i class="fas fa-exclamation-triangle"></i> Različite
                          adrese isporuke</strong
                        >
                        <p class="mb-2 mt-1">
                          Izabrane profakture imaju različite adrese primaoca.
                          Izaberite koju adresu želite da koristite:
                        </p>
                        <div
                          v-for="(addr, aIdx) in availableAddresses"
                          :key="aIdx"
                          class="mb-1"
                        >
                          <b-button
                            size="sm"
                            variant="outline-primary"
                            @click="selectAddress(addr)"
                          >
                            <i class="fas fa-map-marker-alt"></i>
                            {{ addr.label }}
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <h5 class="mb-3">Pošiljalac</h5>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>Naziv *</label>
                        <b-form-input
                          v-model="form.sender.name"
                          type="text"
                          maxlength="35"
                        />
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Poštanski broj *</label>
                        <b-form-input
                          v-model="form.sender.zipcode"
                          type="text"
                          maxlength="10"
                        />
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Kod zemlje *</label>
                        <b-form-input
                          v-model="form.sender.countryCode"
                          type="text"
                          maxlength="3"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>Grad *</label>
                        <b-form-input
                          v-model="form.sender.city"
                          type="text"
                          maxlength="35"
                        />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>Ulica i broj *</label>
                        <b-form-input
                          v-model="form.sender.streetAndNumber"
                          type="text"
                          maxlength="70"
                        />
                      </div>
                    </div>
                  </div>

                  <hr />

                  <h5 class="mb-3">Primalac</h5>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>Naziv *</label>
                        <b-form-input
                          v-model="form.cosignee.name"
                          type="text"
                          required
                          maxlength="35"
                        />
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Poštanski broj *</label>
                        <b-form-input
                          v-model="form.cosignee.zipcode"
                          type="text"
                          required
                          maxlength="10"
                        />
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Kod zemlje *</label>
                        <b-form-input
                          v-model="form.cosignee.countryCode"
                          type="text"
                          required
                          maxlength="3"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>Grad *</label>
                        <b-form-input
                          v-model="form.cosignee.city"
                          type="text"
                          required
                          maxlength="35"
                        />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>Ulica i broj *</label>
                        <b-form-input
                          v-model="form.cosignee.streetAndNumber"
                          type="text"
                          required
                          maxlength="70"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>Telefon</label>
                        <b-form-input
                          v-model="form.cosignee.telephone"
                          type="text"
                          maxlength="30"
                        />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>Mobilni (SMS obaveštenje)</label>
                        <b-form-input
                          v-model="form.cosignee.notifyGSM"
                          type="text"
                          maxlength="35"
                        />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>Email obaveštenje</label>
                        <b-form-input
                          v-model="form.cosignee.notifyEmail"
                          type="email"
                          maxlength="70"
                        />
                      </div>
                    </div>
                  </div>

                  <hr />

                  <h5 class="mb-3">Detalji pošiljke</h5>
                  <div class="row">
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Broj paketa *</label>
                        <b-form-input
                          v-model.number="form.numberOfCollies"
                          type="number"
                          min="1"
                          max="999"
                          required
                        />
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Tip jedinice</label>
                        <b-form-select v-model="form.unitType">
                          <option value="Weight">Težina (kg)</option>
                          <option value="Pallet">Paleta</option>
                          <option value="Quantity">Količina</option>
                          <option value="Letter">Pismo</option>
                          <option value="BoxA">Kutija A</option>
                          <option value="BoxB">Kutija B</option>
                          <option value="BoxC">Kutija C</option>
                        </b-form-select>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Vrednost jedinice</label>
                        <b-form-input
                          v-model.number="form.unitAmount"
                          type="number"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Otkupnina (COD)</label>
                        <b-form-input
                          v-model.number="form.codValue"
                          type="number"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>Referenca 1</label>
                        <b-form-input
                          v-model="form.ref1"
                          type="text"
                          maxlength="35"
                        />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>Referenca 2</label>
                        <b-form-input
                          v-model="form.ref2"
                          type="text"
                          maxlength="35"
                        />
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>Referenca 3</label>
                        <b-form-input
                          v-model="form.ref3"
                          type="text"
                          maxlength="35"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12">
                      <div class="form-group">
                        <label>Napomena</label>
                        <b-form-textarea
                          v-model="form.remark"
                          rows="2"
                          maxlength="150"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>Napomena za preuzimanje</label>
                        <b-form-input
                          v-model="form.pickupRemark"
                          type="text"
                          maxlength="255"
                        />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>Napomena za dostavu</label>
                        <b-form-input
                          v-model="form.deliveryRemark"
                          type="text"
                          maxlength="255"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row mb-3">
                    <div class="col-md-6">
                      <b-form-checkbox v-model="form.cargo" class="mr-3">
                        Kargo
                      </b-form-checkbox>
                      <b-form-checkbox v-model="form.returnDocument">
                        Povratni dokument
                      </b-form-checkbox>
                    </div>
                  </div>

                  <hr />

                  <div class="text-right">
                    <b-button
                      type="button"
                      variant="secondary"
                      @click="resetForm()"
                    >
                      Poništi
                    </b-button>
                    <b-button type="submit" variant="primary" class="ml-2">
                      Kreiraj pošiljku
                    </b-button>
                  </div>
                </div>
              </div>
            </b-form>
          </div>
        </div>
      </b-tab>

      <!-- Tab: Tracking -->
      <b-tab title="Praćenje">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-md-4">
                <div class="form-group">
                  <label>ID pošiljke</label>
                  <b-input-group>
                    <b-form-input
                      v-model="trackingShipmentId"
                      type="text"
                      placeholder="Unesite ID pošiljke"
                      @keyup.enter="trackShipment"
                    />
                    <b-input-group-append>
                      <b-button variant="primary" @click="trackShipment">
                        <i class="fas fa-search"></i> Pretraži
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
              </div>
            </div>

            <div v-if="trackingResult" class="row">
              <div class="col-md-6">
                <div class="card border">
                  <div class="card-header bg-light">
                    <strong>Informacije o pošiljci</strong>
                  </div>
                  <div class="card-body">
                    <table class="table table-sm table-borderless mb-0">
                      <tbody>
                        <tr>
                          <td class="text-muted" style="width: 40%">
                            ID Pošiljke:
                          </td>
                          <td>
                            <strong>{{ trackingResult.ShipmentID }}</strong>
                          </td>
                        </tr>
                        <tr>
                          <td class="text-muted">Cargo ID:</td>
                          <td>{{ trackingResult.FullCargoID || "—" }}</td>
                        </tr>
                        <tr>
                          <td class="text-muted">Status:</td>
                          <td>
                            <b-badge
                              :variant="
                                getStatusVariant(trackingResult.APIStatus)
                              "
                            >
                              {{
                                getStatusLabel(
                                  trackingResult.APIStatus,
                                  trackingResult,
                                )
                              }}
                            </b-badge>
                          </td>
                        </tr>
                        <tr>
                          <td class="text-muted">Datum kreiranja:</td>
                          <td>{{ formatDate(trackingResult.CreateDate) }}</td>
                        </tr>
                        <tr>
                          <td class="text-muted">Datum slanja:</td>
                          <td>{{ formatDate(trackingResult.SendDate) }}</td>
                        </tr>
                        <tr v-if="trackingResult.Cosignee">
                          <td class="text-muted">Primalac:</td>
                          <td>
                            {{ trackingResult.Cosignee.Name }},
                            {{ trackingResult.Cosignee.Place }}
                          </td>
                        </tr>
                        <tr v-if="trackingResult.Sender">
                          <td class="text-muted">Pošiljalac:</td>
                          <td>
                            {{ trackingResult.Sender.Name }},
                            {{ trackingResult.Sender.Place }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="card border">
                  <div class="card-header bg-light">
                    <strong>Istorija statusa</strong>
                  </div>
                  <div class="card-body p-0">
                    <div
                      v-if="
                        trackingResult.Events &&
                          trackingResult.Events.length > 0
                      "
                    >
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Datum</th>
                            <th>Status</th>
                            <th>Opis</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(event, idx) in trackingResult.Events"
                            :key="idx"
                          >
                            <td>{{ formatDateTime(event.TimeOfScan) }}</td>
                            <td>{{ event.StatusName || "—" }}</td>
                            <td>
                              {{
                                event.StatusDescription ||
                                  event.StatusDescriptionEN ||
                                  "—"
                              }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-else class="p-3 text-muted text-center">
                      Nema zabeleženih događaja.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <!-- Shipment Detail Modal -->
    <b-modal
      v-model="showDetailModal"
      :title="
        selectedShipment
          ? `Pošiljka #${selectedShipment.ShipmentID}`
          : 'Detalji pošiljke'
      "
      size="lg"
      ok-only
      ok-title="Zatvori"
    >
      <div v-if="selectedShipment">
        <div class="row">
          <div class="col-md-6">
            <h6>Osnovni podaci</h6>
            <table class="table table-sm table-borderless">
              <tbody>
                <tr>
                  <td class="text-muted">ID Pošiljke:</td>
                  <td>
                    <strong>{{ selectedShipment.ShipmentID }}</strong>
                  </td>
                </tr>
                <tr>
                  <td class="text-muted">Cargo ID:</td>
                  <td>{{ selectedShipment.FullCargoID || "—" }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Status:</td>
                  <td>
                    <b-badge
                      :variant="getStatusVariant(selectedShipment.APIStatus)"
                    >
                      {{
                        getStatusLabel(
                          selectedShipment.APIStatus,
                          selectedShipment,
                        )
                      }}
                    </b-badge>
                  </td>
                </tr>
                <tr>
                  <td class="text-muted">Kreirana:</td>
                  <td>{{ formatDate(selectedShipment.CreateDate) }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Poslata:</td>
                  <td>{{ formatDate(selectedShipment.SendDate) }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Referenca 1:</td>
                  <td>{{ selectedShipment.Ref1 || "—" }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Referenca 2:</td>
                  <td>{{ selectedShipment.Ref2 || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="col-md-6">
            <h6>Primalac</h6>
            <table
              v-if="selectedShipment.Cosignee"
              class="table table-sm table-borderless"
            >
              <tbody>
                <tr>
                  <td class="text-muted">Naziv:</td>
                  <td>{{ selectedShipment.Cosignee.Name || "—" }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Mesto:</td>
                  <td>{{ selectedShipment.Cosignee.Place || "—" }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Ulica:</td>
                  <td>{{ selectedShipment.Cosignee.Street || "—" }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Telefon:</td>
                  <td>{{ selectedShipment.Cosignee.Telephone || "—" }}</td>
                </tr>
              </tbody>
            </table>

            <h6 class="mt-3">Pošiljalac</h6>
            <table
              v-if="selectedShipment.Sender"
              class="table table-sm table-borderless"
            >
              <tbody>
                <tr>
                  <td class="text-muted">Naziv:</td>
                  <td>{{ selectedShipment.Sender.Name || "—" }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Mesto:</td>
                  <td>{{ selectedShipment.Sender.Place || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="selectedShipmentEvents.length > 0" class="mt-3">
          <h6>Istorija statusa</h6>
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Status</th>
                <th>Opis</th>
                <th>Napomena</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(event, idx) in selectedShipmentEvents" :key="idx">
                <td>{{ formatDateTime(event.TimeOfScan) }}</td>
                <td>{{ event.StatusName || "—" }}</td>
                <td>
                  {{
                    event.StatusDescription || event.StatusDescriptionEN || "—"
                  }}
                </td>
                <td>{{ event.Remark || "—" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>

    <!-- Profaktura Detail Modal -->
    <b-modal
      v-model="showProfakturaModal"
      :title="
        profakturaModalDoc
          ? `Profaktura ${profakturaModalDoc.dokument}`
          : 'Detalji profakture'
      "
      size="lg"
      ok-only
      ok-title="Zatvori"
    >
      <div v-if="profakturaModalDoc">
        <div class="row">
          <div class="col-md-6">
            <h6>Dokument</h6>
            <table class="table table-sm table-borderless">
              <tbody>
                <tr>
                  <td class="text-muted" style="width: 40%">Broj:</td>
                  <td>
                    <strong>{{ profakturaModalDoc.dokument }}</strong>
                  </td>
                </tr>
                <tr>
                  <td class="text-muted">Datum:</td>
                  <td>{{ profakturaModalDoc.datum || "—" }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Način isporuke:</td>
                  <td>{{ profakturaModalDoc.nacinIsporuke || "—" }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Komercijalista:</td>
                  <td>{{ profakturaModalDoc.komercijalista || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="col-md-6">
            <h6>Iznosi</h6>
            <table class="table table-sm table-borderless">
              <tbody>
                <tr>
                  <td class="text-muted" style="width: 40%">Osnovica:</td>
                  <td>
                    {{ formatCurrency(profakturaModalDoc.total.osnovica) }} RSD
                  </td>
                </tr>
                <tr>
                  <td class="text-muted">Vrednost:</td>
                  <td>
                    {{ formatCurrency(profakturaModalDoc.total.vrednost) }} RSD
                  </td>
                </tr>
                <tr>
                  <td class="text-muted">Za uplatu:</td>
                  <td>
                    <strong
                      >{{
                        formatCurrency(profakturaModalDoc.total.uplatiti)
                      }}
                      RSD</strong
                    >
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <hr />
        <div class="row">
          <div class="col-md-6">
            <h6>Kupac</h6>
            <strong>{{ profakturaModalDoc.kupac.naziv }}</strong
            ><br />
            <small>{{ profakturaModalDoc.kupac.adr1 }}</small
            ><br />
            <small>{{ profakturaModalDoc.kupac.adr2 }}</small>
            <small v-if="profakturaModalDoc.kupac.adr3"
              ><br />{{ profakturaModalDoc.kupac.adr3 }}</small
            >
          </div>
          <div class="col-md-6">
            <h6>Adresa isporuke</h6>
            <div v-if="profakturaModalDoc.hasDeliveryAddress">
              <strong>{{ profakturaModalDoc.objekat.naziv }}</strong
              ><br />
              <small>{{ profakturaModalDoc.objekat.adr1 }}</small
              ><br />
              <small>{{ profakturaModalDoc.objekat.adr2 }}</small>
            </div>
            <div v-else>
              <small class="text-muted">Isporuka na adresu kupca</small>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<style scoped>
.nav-tabs .nav-link.active {
  font-weight: 600;
}
.autocomplete {
  width: 100%;
}
</style>
